/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import type { AxiosRequestConfig } from 'axios';

import { ActionManagerInstance } from '../actions/manager';
import { resolveRestApi } from '../router/app-router';
import { fetch as axiosFetch } from '../service_broker/fetch';
import * as actionType from '../store/state/actions/index';
import { ACTION_OPENWORKBYHANDLE } from '../store/state/actions/index';
import { getActiveContainerItemName, getContainerData } from '../container/container-utils';
import * as StateManager from '../store/state/state-manager';
import Constants, { publicConstants } from '../constants';
import type { CaseOptions } from '../mashup/types';
import type { Headers } from '../types';
import EnvironmentInfo from '../environment-info';

import RemoteCase from './remote-case';

const { RESOURCE_TYPES } = publicConstants;

class CaseAPIs {
  /**
   * Open the work object associated with the given workID into the page associated with the pageID
   * @param workID The work object to be opened<br>
   * Example: "OPB1HW-SPACE-WORK RA-10001"<br><br>
   * @param context The current context where to render
   * @param target The target container - primary
   * @param options - object containing extra infomation like startingFields and pageName
   * @param options.pageName  - Name of the page like pyDetails, pyEmbedAssignment
   * @param options.channelName  - Channel Name
   * @returns A promise associated with the action (and stored in ActionManager)

   */
  static openCase(
    workID: string,
    context: string,
    target: string,
    options: { pageName?: string; channelName?: string } = {}
  ) {
    const actionInfo: {
      insKey: string;
      acTargetName: string;
      actionMgrID: string;
      context: string;
      headers?: Headers;
    } = {
      insKey: workID,
      acTargetName: target,
      actionMgrID: ActionManagerInstance.getNextActionMgrId(),
      context,
      ...options
    };
    const theType = actionType.OPEN_WORK_BY_HANDLE;
    if (options && options.channelName) {
      actionInfo.headers = { [Constants.X_ORIGIN_CHANNEL]: options.channelName };
    } else if (EnvironmentInfo.isWSSPortal()) {
      actionInfo.headers = { [Constants.X_ORIGIN_CHANNEL]: 'WSS' };
    }
    const { promise } = ActionManagerInstance.actionManagerPromise(theType, actionInfo.actionMgrID);

    StateManager.dispatch({
      type: theType,
      payload: actionInfo
    });

    return promise;
  }

  /**
   * Open the given assignment and associated with the given container target
   * @param assignmentId The ID of the assignment to be opened.<br>
   * Example: "ASSIGN-WORKLIST OPB1HW-SPACE-WORK RA-43001!REQUEST_FLOW_0"
   * <br><br>
   * @param context The current context where to render
   * @param target The target container - primary
   * @param options - object containing extra infomation like startingFields and pageName
   * @param options.pageName  - Name of the page like pyDetails, pyEmbedAssignment
   * @param options.channelName  -  Channel Name
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  static openAssignment(
    assignmentId: string,
    context: string,
    target: string,
    options: { pageName?: string; channelName?: string } = {}
  ) {
    const actionInfo: {
      assignmentID: string;
      vcName: string;
      doDeleteCaseSummary: boolean;
      actionMgrID: string;
      context: string;
      headers?: Headers;
    } = {
      assignmentID: assignmentId,
      vcName: target,
      doDeleteCaseSummary: true,
      actionMgrID: ActionManagerInstance.getNextActionMgrId(),
      context,
      ...options
    };
    const theType = actionType.OPEN_ASSIGNMENT;
    if (options && options.channelName) {
      actionInfo.headers = { [Constants.X_ORIGIN_CHANNEL]: options.channelName };
    } else if (EnvironmentInfo.isWSSPortal()) {
      actionInfo.headers = { [Constants.X_ORIGIN_CHANNEL]: 'WSS' };
    }
    const { promise } = ActionManagerInstance.actionManagerPromise(theType, actionInfo.actionMgrID);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionInfo }
    });

    return promise;
  }

  /**
   *
   * Create a work object for the given <strong>class</strong> and <strong>flow</strong>.
   * Once created, show the work object in the given container <strong>target</strong>.
   *
   * @param workClass The ID class.<br>
   * Example: "ASSIGN-WORKLIST OPB1HW-SPACE-WORK RA-43001!REQUEST_FLOW_0"
   * <br><br>
   * @param context The current context where to render
   * @param target The target container - primary
   * @param options - object containing extra information like startingFields
   * @param options.channelName - Channel Name
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  static createCase(workClass: string, context: string, target: string, options: CaseOptions = {}) {
    const theType = actionType.CREATE_WORK;

    const actionInfo: {
      renderInline?: boolean | undefined;
      pageName?: string | undefined;
      disableAssignmentHeader?: boolean | undefined;
      defaultCasePage?: string | undefined;
      channelName?: string | undefined;
      class: string;
      acTargetName: string;
      actionMgrID: string;
      context: string;
      headers?: Headers;
    } = {
      class: workClass,
      acTargetName: target,
      actionMgrID: ActionManagerInstance.getNextActionMgrId(),
      context,
      ...options
    };

    if (options && options.channelName) {
      actionInfo.headers = { [Constants.X_ORIGIN_CHANNEL]: options.channelName };
    } else if (EnvironmentInfo.isWSSPortal()) {
      actionInfo.headers = { [Constants.X_ORIGIN_CHANNEL]: 'WSS' };
    }

    const { promise } = ActionManagerInstance.actionManagerPromise(theType, actionInfo.actionMgrID);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionInfo }
    });

    return promise;
  }

  /**
   *
   * Open the next urgent assignment for the user
   *
   * @param context The current context where to render
   * @param target The target container - primary
   * @param options - object containing extra information
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  static getNextWork(context: string, target: string, options: object = {}) {
    const theType = actionType.OPEN_ASSIGNMENT;

    const actionInfo = {
      isGetNextWork: true,
      vcName: target,
      doDeleteCaseSummary: true,
      actionMgrID: ActionManagerInstance.getNextActionMgrId(),
      context,
      ...options
    };
    const { promise } = ActionManagerInstance.actionManagerPromise(theType, actionInfo.actionMgrID);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionInfo }
    });

    return promise;
  }

  static getOpenedCaseItems(target: string) {
    const caseItems: {
      key: string;
      workID: string;
      caseID: string;
      className: string;
      context: string;
      containerItemID: string;
      target: string;
      active: boolean;
      resourceType: string;
    }[] = [];
    const containerData = getContainerData(target);
    if (containerData) {
      const { items = {} } = containerData;
      const itemKeys = Object.keys(items);
      const activeItemName = getActiveContainerItemName(target);
      itemKeys.forEach((itemName) => {
        if (
          items[itemName] &&
          (items[itemName].resourceType === RESOURCE_TYPES.CASE ||
            items[itemName].resourceType === RESOURCE_TYPES.ASSIGNMENT ||
            items[itemName].resourceType === RESOURCE_TYPES.OBJECT)
        ) {
          const { key = '', context } = items[itemName];
          const item = items[itemName];
          const caseID = key.substring(key.indexOf(' ') + 1);
          const className = StateManager.getValue('.caseTypeID', Constants.CASE_INFO.CASE_INFO, context);
          const active = activeItemName === itemName;
          caseItems.push({
            ...item,
            key,
            workID: key,
            caseID,
            className,
            context,
            containerItemID: itemName,
            target,
            active
          });
        }
      });
    }
    return caseItems;
  }

  /**
     * Fetch case details for a given case id
     * @param caseID The case details to be fetched<br>
     * @param caseClass Class of the case. This parameter is optional for normal cases but mandatory for remote cases to succesfully fetch case details<br>
     * Example: "OPB1HW-SPACE-WORK RA-43001"<br><br>
     * @returns - Case details of given case id

    */
  static getCaseDetails = (caseID: string, caseClass?: string): Promise<object> => {
    if (!caseID) {
      return Promise.reject(new Error('Case ID is required'));
    }
    const reqPayload = {
      caseID
    };
    const { restApi, reqType } = resolveRestApi(ACTION_OPENWORKBYHANDLE, reqPayload);
    const headers: AxiosRequestConfig['headers'] = {};
    const remoteSystemID = RemoteCase.getRemoteHeaderFromSourceClass(caseClass ?? '');
    if (remoteSystemID) {
      headers.RemoteSystemID = remoteSystemID;
    }
    return axiosFetch(restApi, {
      method: reqType,
      headers
    });
  };

  /**
   * Fetch case-wide bulk actions for given caseIds list
   * @param caseIDList The caseIds list<br>
   * Example: ["OPB1HW-SPACE-WORK RA-43001"]<br><br>
   * @returns - all actions eligible for bulk processing for given caseIds
   */
  static getCaseBulkActions = async (caseIDList: string[]): Promise<object[]> => {
    const { restApi, reqType } = resolveRestApi(actionType.ACTION_GET_BULK_ACTIONS);
    const casesList = caseIDList.map((caseID) => ({ ID: caseID }));
    const casesListLength = casesList.length;
    const chunkSize = casesListLength / 5;
    const reqBody: { cases?: { ID: string }[] } = {};
    const requests: Promise<any>[] = [];
    for (let i = 0; i < casesListLength; i += chunkSize) {
      reqBody.cases = casesList.slice(i, i + chunkSize);

      if (reqBody.cases.length) {
        requests.push(
          axiosFetch(restApi, {
            method: reqType,
            body: reqBody
          })
        );
      }
    }
    const response: { data: { availableActions: { ID: string }[] } }[] = await Promise.all(requests);
    let availableActions: { ID: string }[] = [];
    for (let i = 0; i < response.length; i += 1) {
      if (i === 0) {
        availableActions = response[i].data.availableActions;
      } else if (availableActions.length) {
        availableActions = availableActions.filter((item1) =>
          response[i].data.availableActions.some((item2) => item1.ID === item2.ID)
        );
      }
    }
    return availableActions;
  };

  /**
   * Fetch case-wide bulk actions for given casetypeID string
   * @param casetypeID Case's casetypeID
   * Example: "ON8TTL-C11nGall-Work-ComponentTest"<br><br>
   * @returns - all actions eligible for asynchronous bulk processing for given casetypeID
   */
  static getCaseBulkActionsByCaseTypeID = async (casetypeID: string): Promise<object[]> => {
    const reqPayload = {
      casetypeID
    };
    // This transforms the route to swap out {{casetypeID}} with actual casetypeID value in the endpoint URL
    const { restApi, reqType } = resolveRestApi(actionType.ACTION_GET_BULK_ACTIONS_BY_CASETYPE, reqPayload);

    const response = await axiosFetch(restApi, {
      method: reqType
    });

    let availableActions = [];
    if (response.data?.availableActions?.length) {
      availableActions = response.data.availableActions;
    }
    return availableActions;
  };
}

export default CaseAPIs;
